import React, { useState, useEffect } from 'react';
import { branchApi, staffApi, formatApiErrorDetail } from '../../lib/api';
import { formatDate, getStatusColor } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Store,
  Plus,
  MoreHorizontal,
  Edit2,
  Loader2,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    branch_name: '',
    location: '',
    manager_user_id: '',
  });

  const fetchData = async () => {
    try {
      const [branchesRes, staffRes] = await Promise.all([
        branchApi.list(),
        staffApi.list(),
      ]);
      setBranches(branchesRes.data.items || branchesRes.data);
      // Filter only store managers for the dropdown
      const sData = staffRes.data;
      setManagers((sData.items || sData).filter(s => s.role === 'STORE_MANAGER'));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      branch_name: '',
      location: '',
      manager_user_id: '',
    });
    setEditingBranch(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleOpenEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      branch_name: branch.branch_name,
      location: branch.location,
      manager_user_id: branch.manager_user_id || '',
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = {
        ...formData,
        manager_user_id: formData.manager_user_id || null,
      };

      if (editingBranch) {
        await branchApi.update(editingBranch.id, dataToSend);
        toast.success('Branch updated');
      } else {
        await branchApi.create(dataToSend);
        toast.success('Branch created');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (branch) => {
    try {
      const newStatus = branch.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await branchApi.update(branch.id, { status: newStatus });
      toast.success(`Branch ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update status');
    }
  };

  const getManagerName = (managerId) => {
    if (!managerId) return 'Not assigned';
    const manager = managers.find(m => m.id === managerId);
    return manager?.name || 'Unknown';
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Branches
            </h1>
            <p className="text-slate-600 mt-1">Manage your store locations</p>
          </div>
          <Button
            onClick={handleOpenCreate}
            className="bg-[#005DE8] hover:bg-[#0047B3]"
            data-testid="create-branch-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Branch
          </Button>
        </div>

        {/* Branches Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : branches.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Branch Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Manager</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {branches.map((branch) => (
                    <TableRow key={branch.id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                            <Store className="h-5 w-5 text-[#005DE8]" />
                          </div>
                          <span className="font-medium text-slate-900">{branch.branch_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <MapPin className="h-4 w-4" />
                          {branch.location}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {getManagerName(branch.manager_user_id)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(branch.status)} border`}>
                          {branch.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(branch.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`branch-actions-${branch.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEdit(branch)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(branch)}>
                              {branch.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Store className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No branches yet</h3>
                <p className="text-slate-500 mt-1">Get started by adding your first branch</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                {editingBranch ? 'Edit Branch' : 'Add New Branch'}
              </DialogTitle>
              <DialogDescription>
                {editingBranch ? 'Update branch details' : 'Create a new branch location'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="branch_name">Branch Name</Label>
                  <Input
                    id="branch_name"
                    value={formData.branch_name}
                    onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })}
                    placeholder="e.g., Main Street Store"
                    required
                    data-testid="branch-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location / Address</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., 123 Main Street, City"
                    required
                    data-testid="branch-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manager">Store Manager (Optional)</Label>
                  <Select
                    value={formData.manager_user_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, manager_user_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger data-testid="branch-manager-select">
                      <SelectValue placeholder="Select a manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No manager</SelectItem>
                      {managers.filter(m => {
                        // Only show managers not assigned to OTHER branches
                        const assignedBranch = branches.find(b => b.manager_user_id === m.id);
                        if (!assignedBranch) return true; // not assigned anywhere
                        if (editingBranch && assignedBranch.id === editingBranch.id) return true; // assigned to THIS branch
                        return false;
                      }).map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  disabled={saving}
                  data-testid="branch-submit-button"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {editingBranch ? 'Update Branch' : 'Create Branch'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
